import React, { useState } from "react";
import Amount from "../../ui-components/molecules/amount/amount";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import ProbillChipInput from "../shipments/labels-receipts/probillChipInput";
import { formatProbillDisplay } from "../shipments/labels-receipts/types";
import { PublicPaymentGroup } from "../../app/data/payment/PaymentSession";
import PublicPayPanel from "./publicPayPanel";
import PublicPayPortalPromo from "./publicPayPortalPromo";
import QuickPayHeader from "./quickPayHeader";
import { PUBLIC_PAY_MAX_INVOICES, usePayableProbills } from "./usePayableProbills";
import "../shipments/labels-receipts/labelsReceipts.scss";
import "./publicPayment.scss";

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const PublicPaymentPage: React.FC = () => {
  const { entries, setEntries, inputValue, setInputValue, groups, lookupError, validating } =
    usePayableProbills();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const onBeforePay = () => {
    if (!EMAIL_PATTERN.test(email.trim())) {
      setEmailError("Enter a valid email address.");
      return false;
    }
    setEmailError(null);
    return true;
  };

  return (
    <div className="xgs-public-payment">
      <QuickPayHeader />
      <div className="xgs-public-payment__content">
        <div className="xgs-public-payment__layout">
        <div className="xgs-public-payment__main">
        <section className="xgs-print-download__card">
          <div className="xgs-print-download__card__header">
            <span className="xgs-print-download__step">1</span>
            <div>
              <div className="xgs-print-download__card__title">Enter XGS probill numbers</div>
              <div className="xgs-print-download__card__desc">
                Type or paste one or more XGS probill numbers. Only an XGS probill number is
                accepted. Each is checked automatically. Up to {PUBLIC_PAY_MAX_INVOICES} XGS
                probill numbers.
              </div>
            </div>
          </div>
          <ProbillChipInput
            entries={entries}
            onEntriesChange={setEntries}
            inputValue={inputValue}
            onInputValueChange={setInputValue}
            maxValid={PUBLIC_PAY_MAX_INVOICES}
            emptyPlaceholder="Type or paste XGS probill numbers…"
            addPlaceholder="Add another XGS probill number…"
            invalidFormatMessage="Enter an XGS probill number"
            capNoun="XGS probill numbers"
          />
        </section>
        {lookupError && (
          <div className="xgs-public-payment__actions">
            <XGSErrorMessage>{lookupError}</XGSErrorMessage>
          </div>
        )}
        {groups.length > 0 && (
          <section className="xgs-print-download__card">
            <div className="xgs-print-download__card__header">
              <span className="xgs-print-download__step">2</span>
              <div>
                <div className="xgs-print-download__card__title">Review and pay</div>
                <div className="xgs-print-download__card__desc">
                  The amount charged is the current open balance.
                </div>
              </div>
            </div>
            {groups.length > 1 && (
              <p className="xgs-public-payment__group-note">
                These invoices are billed to different accounts, so each account is paid separately.
              </p>
            )}
            <label className="xgs-public-payment__label" htmlFor="public-pay-email">
              Email address
            </label>
            <input
              id="public-pay-email"
              className="xgs-public-payment__input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (emailError) {
                  setEmailError(null);
                }
              }}
            />
            {emailError && <p className="xgs-public-payment__field-error">{emailError}</p>}
            {groups.map((group) => (
              <PaymentGroup
                key={group.groupNumber}
                group={group}
                showHeading={groups.length > 1}
                email={email}
                onEmailChange={setEmail}
                payDisabled={validating}
                onBeforePay={onBeforePay}
              />
            ))}
          </section>
        )}
        </div>
        <PublicPayPortalPromo />
        </div>
      </div>
    </div>
  );
};

const PaymentGroup: React.FC<{
  group: PublicPaymentGroup;
  showHeading: boolean;
  email: string;
  onEmailChange: (email: string) => void;
  payDisabled: boolean;
  onBeforePay: () => boolean;
}> = ({ group, showHeading, email, onEmailChange, payDisabled, onBeforePay }) => (
  <div className="xgs-public-payment__group">
    {showHeading && (
      <h2 className="xgs-public-payment__group-title">Payment {group.groupNumber}</h2>
    )}
    <ul className="xgs-public-payment__probills">
      {group.probills.map((line) => (
        <li key={line.probillNumber} className="xgs-public-payment__probill">
          <span>{formatProbillDisplay(line.probillNumber)}</span>
          <Amount amount={line.openAmountCents / 100} />
        </li>
      ))}
    </ul>
    <p className="xgs-public-payment__amount-label">Open amount</p>
    <div className="xgs-public-payment__amount">
      <Amount amount={group.openAmountCents / 100} />
    </div>
    <PublicPayPanel
      email={email}
      onEmailChange={onEmailChange}
      openAmountCents={group.openAmountCents}
      probills={group.probills}
      showProbills
      showEmail={false}
      disabled={payDisabled}
      probillNumbers={group.probills.map((line) => line.probillNumber)}
      onBeforePay={onBeforePay}
    />
  </div>
);

export default PublicPaymentPage;
